import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { OblivionMineBoardView } from "./OblivionMineBoardView";
import { OBLIVION_MINE_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildOblivionMineReplaySnapshots,
  type OblivionMineReplaySnapshot,
} from "./replay-runner";
import "./styles/oblivion-mine-tokens.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const SPEED_OPTIONS = [
  { label: "느리게", ms: 900 },
  { label: "보통", ms: 450 },
  { label: "빠르게", ms: 200 },
] as const;

function clampStepIndex(
  index: number,
  snapshots: readonly OblivionMineReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly OblivionMineReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;
  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }
  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);
  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly OblivionMineReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function eventSummary(snapshot: OblivionMineReplaySnapshot): string {
  const event = snapshot.event;
  if (event.eventType === "init") return `#${event.sequenceNumber} init`;
  if (event.eventType === "LayMines") {
    return `#${event.sequenceNumber} 매설 15개`;
  }
  const payload = event.payload as { to?: { row: number; col: number } };
  const to = payload.to ? `(${payload.to.row},${payload.to.col})` : "";
  const outcome = snapshot.state.lastHit
    ? "💥 피격·복귀"
    : snapshot.state.lastAction?.type === "Move" &&
        snapshot.state.treasures.some(
          (t) =>
            t.row === payload.to?.row &&
            t.col === payload.to?.col &&
            t.collectedBy !== null,
        )
      ? "💎 보물"
      : "이동";
  return `#${event.sequenceNumber} ${to} ${outcome}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = OBLIVION_MINE_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildOblivionMineReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );

  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState<number>(SPEED_OPTIONS[1].ms);
  const [showAllMines, setShowAllMines] = useState(false);

  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;
  const maxIndex = snapshots.length - 1;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  useEffect(() => {
    if (!isPlaying) return;
    if (safeStepIndex >= maxIndex) {
      setIsPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      const next = clampStepIndex(safeStepIndex + 1, snapshots);
      setStepIndex(next);
      replaceStepQuery(next, snapshots);
    }, speedMs);
    return () => clearTimeout(timer);
  }, [isPlaying, safeStepIndex, maxIndex, speedMs, snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const isGameOver = state.phase === "game-over";
  const winnerName = state.winnerId ? namesByPlayerId[state.winnerId] : null;
  const turnName =
    namesByPlayerId[state.currentPlayerId] ?? state.currentPlayerId;
  const phaseLabel = state.phase === "lay-mines" ? "매설" : "이동";
  const minesToRender = showAllMines ? state.mines : current.viewer.mines;

  return (
    <div
      data-testid="oblivion-mine-replay-screen"
      className="min-h-screen px-3 py-4 sm:px-6"
      style={{ background: "var(--cn-oblivion-mine-bg)" }}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="m-0 font-bold text-lg text-text-primary">
              망각의 지뢰 리플레이
            </h1>
            <p className="mt-0.5 text-text-muted text-xs">
              {fixture.players.map((p) => p.name).join(" vs ")} · 시뮬레이션
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
            onClick={() => navigateTo("/")}
          >
            ‹ 뒤로
          </button>
        </div>

        {/* Status / result */}
        {isGameOver ? (
          <div
            data-testid="oblivion-mine-replay-result"
            className="rounded-md border border-accent-blue-light bg-accent-blue-bg px-4 py-3 text-center font-semibold text-accent-blue text-sm"
          >
            🏆 {winnerName ?? state.winnerId} 승리 — 보물{" "}
            {state.scores[state.winnerId ?? "p1"]}/3 ({state.moveNumber}수)
          </div>
        ) : (
          <div className="rounded-md border border-border-subtle bg-surface-secondary px-4 py-2 text-center text-sm text-text-secondary">
            {phaseLabel} 페이즈 · 현재 차례:{" "}
            <span className="font-semibold">{turnName}</span> · 보물 흑{" "}
            {state.scores.p1} · 백 {state.scores.p2}
          </div>
        )}

        {/* Board */}
        <div className="flex justify-center">
          <OblivionMineBoardView
            state={state}
            mines={minesToRender}
            lastHit={state.lastHit}
          />
        </div>

        {/* Recap toggle */}
        <div className="flex items-center justify-center">
          <button
            type="button"
            data-testid="oblivion-mine-replay-recap-toggle"
            data-active={showAllMines ? "true" : "false"}
            onClick={() => setShowAllMines((shown) => !shown)}
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
          >
            {showAllMines ? "지뢰 숨김 (관전 뷰)" : "지뢰 전체 공개 (복기)"}
          </button>
        </div>

        {/* Toolbar */}
        <div
          data-testid="oblivion-mine-replay-toolbar"
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-secondary p-3"
        >
          <p
            data-testid="oblivion-mine-replay-event-summary"
            className="text-center text-sm text-text-primary"
          >
            {eventSummary(current)} ·{" "}
            <span className="text-text-muted">
              {namesByPlayerId[current.event.userId] ?? current.event.userId}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="oblivion-mine-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex - 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              ◀ 이전
            </button>
            <button
              type="button"
              data-testid="oblivion-mine-replay-play-toggle"
              onClick={() => setIsPlaying((playing) => !playing)}
              disabled={safeStepIndex >= maxIndex}
              className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white disabled:opacity-40"
            >
              {isPlaying ? "⏸ 일시정지" : "▶ 자동재생"}
            </button>
            <button
              type="button"
              data-testid="oblivion-mine-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex + 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              다음 ▶
            </button>
            <span
              data-testid="oblivion-mine-replay-step-count"
              className="ml-auto text-text-secondary text-xs"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={maxIndex}
            value={safeStepIndex}
            data-testid="oblivion-mine-replay-step-slider"
            onChange={(event) => {
              setIsPlaying(false);
              setReplayStep(Number(event.target.value));
            }}
            className="w-full"
          />
          <label className="flex items-center justify-end gap-2 text-text-muted text-xs">
            재생 속도
            <select
              data-testid="oblivion-mine-replay-speed"
              value={speedMs}
              onChange={(event) => setSpeedMs(Number(event.target.value))}
              className="rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-text-primary"
            >
              {SPEED_OPTIONS.map((option) => (
                <option key={option.ms} value={option.ms}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
